import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, ListBox, Select, TextField } from '@heroui/react';
import type {
    ChartAccount,
    OperationLineDraft,
    OperationLineSide,
} from '@/api/models';
import type { AccountLabel } from '@/pages/accounting/books/journal/components/journalEntryLines';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { accountOptionLabel, isSlot } from '../../operationEditorMeta';
import { lineBoxCopy } from '../../pairBoxMeta';
import {
    AccountFilterPicker,
    soleResolvedAccount,
} from '../accountFilterPicker';

type LineBoxProps = {
    pairGroup: number;
    side: OperationLineSide;
    line: OperationLineDraft;
    accounts: ChartAccount[];
    /** The whole chart — group headers and leaves — what a slot's filter picker offers. */
    chartAccounts: ChartAccount[];
    /** Account id → code + localized name, both languages, for the slot label auto-suggestion. */
    accountLabels: {
        ka: Map<string, AccountLabel>;
        en: Map<string, AccountLabel>;
    };
    /** A BranchAdmin reads the recipe; the box never opens for them. */
    isReadOnly: boolean;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onLineChange: (patch: Partial<OperationLineDraft>) => void;
    onLineKindChange: (kind: 'fixed' | 'slot') => void;
};

const CAPTION = 'text-[9px] font-bold uppercase tracking-widest text-muted';
const SEGMENT = 'text-[9.5px] font-bold uppercase tracking-widest';

/**
 * One leg of a pair: which side, which account, and how the account is decided.
 *
 * **The box is the field.** Clicking a closed box expands it in place into its
 * own editing state — same corner radius, same column, grown in height, with an
 * accent ring. It is not a drawer and not a modal, because the one thing you
 * always need while editing a posting line is the *other* leg of its pair, and
 * both of those cover it up.
 *
 * Only one box in the whole editor is open at a time (the open line is held by
 * the page hook, not here), and Escape or a click outside commits. There is no
 * separate save: every keystroke is already in the draft, so "commit" means
 * "stop editing", and a Cancel button would imply a rollback the editor does not
 * have.
 *
 * **The Fixed/Slot control is always rendered, open or closed.** It decides
 * whether an account is pinned now or asked for at record time — that is the
 * single most consequential fact about a line — and a control you can only find
 * by hovering is not a control an author can rely on.
 */
export const LineBox: React.FC<LineBoxProps> = ({
    pairGroup,
    side,
    line,
    accounts,
    chartAccounts,
    accountLabels,
    isReadOnly,
    isOpen,
    onOpen,
    onClose,
    onLineChange,
    onLineKindChange,
}) => {
    const { t } = useTranslation('pages');
    const copy = lineBoxCopy(line, accounts);
    const slot = isSlot(line);
    const testId = `${pairGroup}-${side.toLowerCase()}`;
    const boxRef = React.useRef<HTMLDivElement>(null);

    // Whether the Fixed-line `Select` or a slot's `AccountFilterPicker`
    // currently has its own popover open. Both portal their popover outside
    // `boxRef` — see `onOutsideClick` below for why the box needs to know.
    const popoverOpenRef = React.useRef(false);
    const onPopoverOpenChange = React.useCallback((open: boolean) => {
        popoverOpenRef.current = open;
    }, []);

    // Escape closes, and so does a pointer landing anywhere else. Both are
    // bound only while this box is the open one, so the editor never holds a
    // document listener per line.
    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        const onOutsideClick = (event: MouseEvent) => {
            // A Select/Autocomplete popover is portaled outside `boxRef`, so
            // its options are "outside" by pure DOM containment even though
            // they belong to a control that is inside this box. Skipping the
            // close while one is open is what lets a click on an option
            // register as a pick instead of closing the box out from under
            // it — `onPopoverOpenChange`, wired to both controls'
            // `onOpenChange`, is the box's only way to know.
            if (popoverOpenRef.current) {
                return;
            }
            const target = event.target as Node | null;
            if (target && boxRef.current && !boxRef.current.contains(target)) {
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        // Capture phase: a click that closes a Select popover would otherwise
        // land outside this box and close the editor with it.
        document.addEventListener('pointerdown', onOutsideClick, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onOutsideClick, true);
        };
    }, [isOpen, onClose]);

    return (
        <div
            className={cn(
                'flex flex-col gap-2 rounded-2xl border p-3 transition-colors',
                isOpen
                    ? 'border-accent bg-surface ring-3 ring-accent/15'
                    : 'border-separator bg-surface',
                !isOpen && !isReadOnly && 'cursor-text hover:border-border',
                slot && !isOpen && 'bg-accent-soft/20',
            )}
            onClick={isOpen || isReadOnly ? undefined : onOpen}
            ref={boxRef}
            {...getNodeTestIdProp(`operation-editor-line-${testId}`)}
        >
            <div className="flex items-center gap-2">
                <span
                    className={cn(
                        'text-[9.5px] font-bold uppercase tracking-widest',
                        side === 'DEBIT' ? 'text-danger' : 'text-success',
                    )}
                >
                    {t(
                        side === 'DEBIT'
                            ? 'operations.editor.debit'
                            : 'operations.editor.credit',
                    )}
                </span>
                {isOpen ? (
                    <span className="ms-auto text-[9.5px] font-bold uppercase tracking-widest text-accent">
                        {t('operations.editor.box.editing')}
                    </span>
                ) : null}
            </div>

            {isOpen ? (
                <LineFields
                    accountLabels={accountLabels}
                    accounts={accounts}
                    chartAccounts={chartAccounts}
                    onPopoverOpenChange={onPopoverOpenChange}
                    line={line}
                    onLineChange={onLineChange}
                    testId={testId}
                />
            ) : (
                <button
                    className="flex flex-col items-start gap-0.5 text-start"
                    disabled={isReadOnly}
                    onClick={onOpen}
                    type="button"
                    {...getNodeTestIdProp(`operation-editor-open-${testId}`)}
                >
                    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        {copy.code ? (
                            <span className="text-sm font-bold tabular-nums text-foreground">
                                {copy.code}
                            </span>
                        ) : null}
                        {copy.name ? (
                            <span className="text-sm text-foreground">
                                {copy.name}
                            </span>
                        ) : null}
                    </span>
                    <span
                        className={cn(
                            'text-xs',
                            copy.isSlot ? 'text-accent' : 'text-muted',
                        )}
                    >
                        {copy.detail ??
                            t(
                                copy.isSlot
                                    ? 'operations.editor.box.slotHint'
                                    : 'operations.editor.box.fixedHint',
                            )}
                    </span>
                </button>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <KindToggle
                    isReadOnly={isReadOnly}
                    isSlot={slot}
                    onChange={onLineKindChange}
                    testId={testId}
                />
                <span
                    className="text-xs text-muted"
                    {...getNodeTestIdProp(`operation-editor-note-${testId}`)}
                >
                    {t(copy.note.key, copy.note.params)}
                </span>
            </div>
        </div>
    );
};

type KindToggleProps = {
    isSlot: boolean;
    isReadOnly: boolean;
    onChange: (kind: 'fixed' | 'slot') => void;
    testId: string;
};

/**
 * Fixed or Slot, as a two-segment pill.
 *
 * A segmented control rather than a switch: neither state is the "off" one. A
 * fixed line and a slot are both complete, valid answers, and a switch would
 * imply that turning it off returns the line to some neutral default.
 */
const KindToggle: React.FC<KindToggleProps> = ({
    isSlot: slot,
    isReadOnly,
    onChange,
    testId,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex shrink-0 gap-0.5 rounded-full bg-surface-secondary p-0.5"
            role="group"
            {...getNodeTestIdProp(`operation-editor-kind-${testId}`)}
        >
            <button
                aria-pressed={!slot}
                className={cn(
                    SEGMENT,
                    'rounded-full px-2.5 py-1 transition-colors',
                    slot
                        ? 'text-muted hover:text-foreground'
                        : 'bg-surface text-foreground shadow-xs',
                )}
                disabled={isReadOnly}
                onClick={(event) => {
                    event.stopPropagation();
                    onChange('fixed');
                }}
                type="button"
                {...getNodeTestIdProp(`operation-editor-kind-fixed-${testId}`)}
            >
                {t('operations.editor.kindFixed')}
            </button>
            <button
                aria-pressed={slot}
                className={cn(
                    SEGMENT,
                    'rounded-full px-2.5 py-1 transition-colors',
                    slot
                        ? 'bg-surface text-foreground shadow-xs'
                        : 'text-muted hover:text-foreground',
                )}
                disabled={isReadOnly}
                onClick={(event) => {
                    event.stopPropagation();
                    onChange('slot');
                }}
                type="button"
                {...getNodeTestIdProp(`operation-editor-kind-slot-${testId}`)}
            >
                {t('operations.editor.kindSlot')}
            </button>
        </div>
    );
};

type LineFieldsProps = {
    line: OperationLineDraft;
    accounts: ChartAccount[];
    chartAccounts: ChartAccount[];
    accountLabels: {
        ka: Map<string, AccountLabel>;
        en: Map<string, AccountLabel>;
    };
    onLineChange: (patch: Partial<OperationLineDraft>) => void;
    /** Told whenever the fixed-line `Select` or the slot's picker opens or closes its own popover. */
    onPopoverOpenChange: (open: boolean) => void;
    testId: string;
};

/**
 * The open box's fields.
 *
 * A slot gets three: the account picker and the line's role label side by
 * side, then the Georgian and English labels stacked full-width — the two the
 * recorder will actually read, so they get the room. Picking a single account
 * in the picker prefills both — see `handleFilterChange` — so in practice an
 * author usually only touches the picker. A fixed line gets two, because
 * picking the account answers everything the labels were for.
 */
const LineFields: React.FC<LineFieldsProps> = ({
    line,
    accounts,
    chartAccounts,
    accountLabels,
    onLineChange,
    onPopoverOpenChange,
    testId,
}) => {
    const { t } = useTranslation('pages');

    const roleField = (
        <TextField
            className="flex min-w-0 flex-col gap-1"
            onChange={(value) => onLineChange({ roleLabel: value })}
            value={line.roleLabel ?? ''}
            {...getNodeTestIdProp(`operation-editor-role-label-${testId}`)}
        >
            <Label className={CAPTION}>
                {t('operations.editor.roleLabelLabel')}
            </Label>
            <Input
                className="text-xs"
                placeholder={t('operations.editor.roleLabelPlaceholder')}
            />
        </TextField>
    );

    // Suggest, never overwrite: only when the new filter resolves to exactly
    // one account or group, and only into a label the author hasn't already
    // typed something into.
    const handleFilterChange = (codes: string[]) => {
        const patch: Partial<OperationLineDraft> = { accountFilter: codes };
        const resolved = soleResolvedAccount(codes, chartAccounts);
        if (resolved) {
            const nameKa = accountLabels.ka.get(resolved.id)?.name;
            const nameEn = accountLabels.en.get(resolved.id)?.name;
            if (!line.slotLabelKa && nameKa) {
                patch.slotLabelKa = nameKa;
            }
            if (!line.slotLabelEn && nameEn) {
                patch.slotLabelEn = nameEn;
            }
        }
        onLineChange(patch);
    };

    if (!isSlot(line)) {
        return (
            <div className="flex flex-col gap-2">
                <Select
                    className="flex min-w-0 flex-col gap-1"
                    onChange={(next) =>
                        next && onLineChange({ accountId: String(next) })
                    }
                    onOpenChange={onPopoverOpenChange}
                    placeholder={t('operations.editor.accountPlaceholder')}
                    value={line.accountId}
                >
                    <Label className={CAPTION}>
                        {t('operations.editor.accountLabel')}
                    </Label>
                    <Select.Trigger
                        className="text-xs"
                        {...getNodeTestIdProp(
                            `operation-editor-account-${testId}`,
                        )}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {accounts.map((account) => (
                                <ListBox.Item
                                    id={account.id}
                                    key={account.id}
                                    textValue={accountOptionLabel(account)}
                                >
                                    {accountOptionLabel(account)}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>
                {roleField}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <AccountFilterPicker
                accounts={chartAccounts}
                onChange={handleFilterChange}
                onOpenChange={onPopoverOpenChange}
                testId={testId}
                value={line.accountFilter}
            />
            {roleField}

            <TextField
                className="flex min-w-0 flex-col gap-1"
                onChange={(value) => onLineChange({ slotLabelKa: value })}
                value={line.slotLabelKa ?? ''}
                {...getNodeTestIdProp(`operation-editor-slot-ka-${testId}`)}
            >
                <Label className={CAPTION}>
                    {t('operations.editor.slotKaLabel')}
                </Label>
                <Input
                    className="text-xs"
                    placeholder={t('operations.editor.slotKaPlaceholder')}
                />
            </TextField>

            <TextField
                className="flex min-w-0 flex-col gap-1"
                onChange={(value) => onLineChange({ slotLabelEn: value })}
                value={line.slotLabelEn ?? ''}
                {...getNodeTestIdProp(`operation-editor-slot-en-${testId}`)}
            >
                <Label className={CAPTION}>
                    {t('operations.editor.slotEnLabel')}
                </Label>
                <Input
                    className="text-xs"
                    placeholder={t('operations.editor.slotEnPlaceholder')}
                />
            </TextField>
        </div>
    );
};
